import { useQuery } from '@apollo/client'
import { SearchableCombobox, TSearchable } from '../base/combobox'
import { CustomFieldSelectorQuery, SelectedCustomFieldSelectorQuery } from './__generated__/selector_query'
import { Query, SelectedQuery } from './selector_query'
import { useCombobox } from '@/helpers/useCombobox'
import { dedupe } from '@/helpers/dedupe'
import { CustomFieldFilters, CustomFieldResource } from '@/types'

type CustomField = SelectedCustomFieldSelectorQuery['customFields']['nodes'][0]

interface Props {
  value: string
  onChange: (value: TSearchable | null) => void
  invalid: boolean
  filters: CustomFieldFilters
}

export const Selector = ({ value, onChange, invalid, filters }: Props) => {
  const { data: selectedData } = useQuery<SelectedCustomFieldSelectorQuery>(
    SelectedQuery,
    { variables: { id: value || 'blank' } }
  )
  const { data, loading, refetch, fetchMore } = useQuery<CustomFieldSelectorQuery>(
    Query,
    { variables: { filters, first: 10 } }
  )

  const { pageInfo, items } = useCombobox<CustomField>({
    pageInfo: data?.customFields.pageInfo,
    querySelected: selectedData?.customFields.nodes[0],
    queryCollection: data?.customFields.nodes,
    buildSearchable: item => ({
      id: item.id,
      name: item.name
    })
  })

  return (
    <SearchableCombobox
      data={items}
      includeBlank={false}
      invalid={invalid}
      loading={loading}
      moreToFetch={pageInfo.current?.hasNextPage}
      refetch={refetch}
      searchField="nameCont"
      value={items.find(d => d.id == value) || { id: '', name: '' }}
      fetchMore={() => fetchMore({
        variables: { after: pageInfo.current?.endCursor },
        updateQuery(previousData, { fetchMoreResult }) {
          if (Object.keys(previousData).length === 0) return fetchMoreResult
          fetchMoreResult.customFields.nodes = dedupe([
            ...previousData.customFields.nodes,
            ...fetchMoreResult.customFields.nodes
          ])

          return { ...fetchMoreResult }
        }
      })}
      filters={{
        resourceTypeEq: filters.resourceTypeEq || CustomFieldResource.AssetType,
        availableForEq: filters.availableForEq || null
      }}
      onChange={onChange}
    />
  )
}
